import { isAndroidStandAlone } from "./mobile";
import { sanitizeUri, isAbsoluteUrl } from "./urlHelper";
import { isEmbeddedInMsTeamsAppCheck } from "client/TeamsEmbed/isEmbeddedInMsTeamsAppCheck";
import { showPopupBlockedAlert } from "./windowPopupBlockedAlert";
import { never } from "./errors";

type OpenImplementation = (
	url: string | undefined,
	target: string | undefined,
	features: string | undefined
) => Window | null;

let isMicrosoftTeams: boolean = false;

isEmbeddedInMsTeamsAppCheck()
	.then(value => {
		isMicrosoftTeams = value;
	});

export function open(url?: string, target?: string, features?: string): Window | null {
	url = sanitizeUri(url);

	if (window.mobileApp) {
		if (url) {
			let openUrl:string;
			// Mobile app cannot open relative URLs through interop, they must be made absolute.
			if (isAbsoluteUrl(url)){
				openUrl = url;
			} else {
				openUrl = new URL(url, document.baseURI).href;
			}
			window.mobileApp
				.openInExternalBrowser(openUrl);
		}
		return null;
	}

	let implementationToUse: OpenImplementation = openDefaultImplementation;
	if (isAndroidStandAlone() || isMicrosoftTeams) {
		implementationToUse = openAndroidStandaloneOrMicrosoftTeamsDesktopImplementation;
	}

	return implementationToUse(url, target, features);
}

function openDefaultImplementation(
	url: string | undefined,
	target: string | undefined,
	features: string | undefined
): Window | null {
	const result: OpenResult = openInternal(
		"",
		target,
		features
	);

	const newWindow: Window | null = handleOpenResultAndGetWindow(
		result,
		url,
		target
	);

	if (newWindow) {
		// Mitigation against hijacking of the current window. See #28368
		newWindow.opener = null;
		if (url) {
			newWindow.location.href = url;
		}
	}

	return newWindow;
}

// Our regular way of mitigating opener hijacking doesn't work for android standalone as it blocks the empty url
// used for the original window.open(). Opening "about:blank" explicitly does not block it, but results in that you cannot
// set the actual location of the new window as it is a different "domain".
// - Workaround 1:
//		- in window.open() load a page on the current domain
//		- wait for the load event
//		- navigate to the actual location
//		NOTE: this add an extra page/endpoint to load which need to exist and can have errors loading as well
// - Workaround 2:
//		- use the window features "noopener" and "noreferrer"
//		- use the actual location in window.open()
//		NOTE: it will result in a null window object being returned
// For now we picked Workaround 2 as we focus on url navigation and specifically chrome which supports
// these window features
function openAndroidStandaloneOrMicrosoftTeamsDesktopImplementation(
	url?: string,
	target?: string,
	features?: string
): Window | null {
	let featureSegments: string[] = [];
	if (features) {
		featureSegments = features.split(",");
	}

	// Mitigation against hijacking of the current window. See #28368
	// NOTE: these features are not available in all browsers
	featureSegments.push("noopener");
	featureSegments.push("noreferrer");

	features = featureSegments.join(",");

	const result: OpenResult = openInternal(url, target, features);

	return handleOpenResultAndGetWindow(
		result,
		url,
		target
	);
}

/**
 * !!WARNING!!: this function should only be used in cases where we need special behavior
 * which is only available through "window.open" AND you are sure the url will not
 * hijack the current window
 */
export function openUnsafe(absoluteUrl: string, target?: string, features?: string): Window | null {
	absoluteUrl = sanitizeUri(absoluteUrl);

	if (window.mobileApp) {

		window.mobileApp
			.openInExternalBrowser(absoluteUrl);

		return null;
	}

	const result: OpenResult = openInternal(absoluteUrl, target, features);

	return handleOpenResultAndGetWindow(
		result,
		absoluteUrl,
		target
	);
}

function openInternal(
	url: string | undefined,
	target: string | undefined,
	features: string | undefined
): OpenResult {
	// IE (11) opens a castrated window (#28601) if features  is undefined (instead it wants an empty string it appears)
	const featuresToUse = typeof features === "undefined" ? "" : features;
	// Similar to the features, IE (11) will throw "Access Denied" when target is undefined and you open more than one window
	const targetToUse = typeof target === "undefined" ? "" : target;

	const newWindow: Window | null = window.open(url, targetToUse, featuresToUse);

	// When noopener is set as window feature, window.open will return null (see the specs).
	// https://html.spec.whatwg.org/multipage/window-object.html#apis-for-creating-and-navigating-browsing-contexts-by-name:disowned-its-opener
	// Also note that when noreferrer is set, noopener will automatically be set as well

	const nullIsExpected: boolean = featuresToUse.indexOf("noopener") !== -1 || featuresToUse.indexOf("noreferrer") !== -1;

	// window.open can return null. E.g. when the popup was blocked
	if (!nullIsExpected && !newWindow) {
		return {
			kind: "popup-blocked"
		};
	}

	return {
		kind: "success",
		window: newWindow
	};
}

function handleOpenResultAndGetWindow(
	result: OpenResult,
	url: string | undefined,
	target: string | undefined
): Window | null {
	switch (result.kind) {
		case "success":
			return result.window;
		case "popup-blocked":
			showPopupBlockedAlert(
				url,
				target
			);
			return null;
		default:
			never(result);
	}
}

type OpenResult = {
	kind: "popup-blocked";
} | {
	kind: "success";
	window: Window | null;
};